import { Box, Button, Link, Text, Tooltip } from "@chakra-ui/react";
import { EdgeOverrideAmount } from "features/fund-family/setup/fund-structure/hooks/useAllocationRuleGraphForm";
import { useInvestmentListSuspense } from "gen/endpoints/investment/investment";
import {
  useTransactionCalculationListSuspense,
  useTransactionCodeConfigListSuspense,
} from "gen/endpoints/transactions/transactions";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  ContentBlock,
  ContentBlockBody,
  ContentBlockGroup,
} from "shared/components/content/ContentBlock";
import { FormSelect } from "shared/components/input/select/Select";
import { SelectOption } from "shared/components/input/select/SelectOption";
import { FormTextInput } from "shared/components/input/text/TextInput";
import { tryParseDecimal } from "shared/utils/DecimalUtils";

export const AllocationRuleGraphFormFields: React.FC<{
  onAddOptOuts: () => void;
  onAddCustomEdgeWeightOverrides: () => void;
  fundFamilyId: string;
  isOptOutsEnabled: boolean;
  isCustomRuleGraph: boolean;
  edgeOverrideAmounts?: EdgeOverrideAmount[];
}> = ({
  onAddOptOuts,
  fundFamilyId,
  isOptOutsEnabled,
  onAddCustomEdgeWeightOverrides,
  isCustomRuleGraph,
  edgeOverrideAmounts,
}) => {
  const hasOptOuts = edgeOverrideAmounts?.some((override: EdgeOverrideAmount) =>
    tryParseDecimal(override.overrideAmount)?.isZero(),
  );

  const { t } = useTranslation(["allocation"]);

  const { data: calculations } = useTransactionCalculationListSuspense(
    fundFamilyId,
    {
      exclude_calculations_with_point_in_time_codes: true,
      classifications_to_exclude: ["hypothetical_waterfall_metric"],
    },
  );

  const { data: transactionCodeConfigs } =
    useTransactionCodeConfigListSuspense(fundFamilyId);

  const { data: investments } = useInvestmentListSuspense(fundFamilyId);

  return (
    <Box flexGrow={1}>
      <FormFieldContainer>
        <FormTextInput
          name="name"
          title={t("allocation:ruleGraphEdit.namePlaceholder")}
        />
      </FormFieldContainer>
      <FormFieldContainer>
        <Text textStyle="small" mb="4px">
          {t("allocation:ruleGraphEdit.useDirections")}
        </Text>
        <Box
          pt="16px"
          pr="16px"
          pl="16px"
          pb="1px"
          bg="gray.25"
          borderWidth="1px"
          borderColor="gray.300"
          borderStyle="dashed"
          borderRadius="8px"
        >
          <FormSelect
            name="transactionCodeId"
            title={t("allocation:ruleGraphEdit.transactionCodePlaceholder")}
          >
            {transactionCodeConfigs.map(({ transaction_code_id, name }) => (
              <SelectOption
                key={transaction_code_id}
                value={transaction_code_id}
              >
                {name}
              </SelectOption>
            ))}
          </FormSelect>
          <FormSelect
            name="investmentId"
            title={t("allocation:ruleGraphEdit.investmentPlaceholder")}
          >
            {investments.map(({ id, legal_name }) => (
              <SelectOption key={id} value={id}>
                {legal_name}
              </SelectOption>
            ))}
          </FormSelect>
        </Box>
      </FormFieldContainer>
      <FormFieldContainer>
        {isCustomRuleGraph ? (
          <Text textStyle="bodyBold" color="primary.700" pb="8px">
            {t("allocation:customRule.added")}
          </Text>
        ) : (
          <FormSelect
            name="calculationId"
            title={t("allocation:ruleGraphEdit.calculationPlaceholder")}
          >
            {calculations.map(({ id, name }) => (
              <SelectOption key={id} value={id}>
                {name}
              </SelectOption>
            ))}
          </FormSelect>
        )}
        <Link
          colorScheme="gray"
          size="sm"
          onClick={onAddCustomEdgeWeightOverrides}
        >
          {isCustomRuleGraph
            ? t("allocation:customRule.editOrRemove")
            : t("allocation:ruleGraphEdit.addCustomRuleDirections")}
        </Link>
      </FormFieldContainer>
      <ContentBlockGroup>
        <ContentBlock>
          <ContentBlockBody display="flex" columnGap="4px" alignItems="center">
            {hasOptOuts ? ( // if there are opt outs, only show the edit link
              <Link colorScheme="gray" size="sm" onClick={onAddOptOuts}>
                {t("allocation:ruleGraphEdit.editOptOuts")}
              </Link>
            ) : isOptOutsEnabled ? ( // if opt outs are enabled, show the add button
              <>
                <Button
                  onClick={onAddOptOuts}
                  size="xs"
                  colorScheme="secondary"
                >
                  {t("allocation:ruleGraphEdit.addOptOuts")}
                </Button>
                <Text textStyle="helper" fontSize="sm" color="gray.500">
                  ({t("allocation:ruleGraphEdit.addOptOutsDescription")})
                </Text>
              </>
            ) : (
              // otherwise, show the add button in disabled state AND wrapped with a tooltip explaining why it's disabled
              <>
                <Tooltip
                  key="tooltip"
                  label={t("allocation:ruleGraphEdit.optOutTooltip")}
                  placement="top"
                >
                  <Button
                    onClick={onAddOptOuts}
                    size="xs"
                    colorScheme="secondary"
                    isDisabled={true}
                  >
                    {t("allocation:ruleGraphEdit.addOptOuts")}
                  </Button>
                </Tooltip>
                <Text textStyle="helper" fontSize="sm" color="gray.500">
                  ({t("allocation:ruleGraphEdit.addOptOutsDescription")})
                </Text>
              </>
            )}
          </ContentBlockBody>
        </ContentBlock>
      </ContentBlockGroup>
    </Box>
  );
};

const FormFieldContainer = ({ children }: { children: React.ReactNode }) => (
  <ContentBlockGroup>
    <ContentBlock>
      <ContentBlockBody pt="40px" pb="40px" pl="64px" pr="64px">
        {children}
      </ContentBlockBody>
    </ContentBlock>
  </ContentBlockGroup>
);
